import React, {useMemo} from 'react';
import {
  Box,
  Button,
  Checkbox,
  Circle,
  Flex,
  HStack,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Tag,
  TagCloseButton,
  TagLabel,
  Text,
  Tooltip,
  Wrap,
  WrapItem,
} from '@chakra-ui/react';
import {useTranslation} from 'react-i18next';
import {TPath, useTypedQuery} from '@synako/typed-path';
import {FiltresItemCatalogueGrandCompte} from 'ff-common';
import {ChevronDownIcon} from '@chakra-ui/icons';
import {SearchCatalogueState} from 'api-types/ffconnect';

// Define the types for facets and pagination based on the inferred data type
type Facets = SearchCatalogueState['facets'];
type Pagination = SearchCatalogueState['pagination'];

interface FilterOptionsBarProps {
  filtersHook: ReturnType<
    typeof useTypedQuery<TPath<{Query: FiltresItemCatalogueGrandCompte}>>
  >;
  facets: Facets | undefined;
  pagination: Pagination | undefined;
  backoffice?: boolean;
}

/**
 * Barre des options de filtrage pour le catalogue grand compte
 */
export const FilterOptionsBar: React.FC<FilterOptionsBarProps> = ({
  filtersHook,
  facets,
  pagination,
  backoffice = false,
}) => {
  const {t} = useTranslation();
  const {params, changeMultiple} = filtersHook;

  const selectedLabels = params.labels ?? [];

  // Check if any filters are active
  const hasActiveFilters = useMemo(() => {
    return !!(
      params.est_TAC ??
      (params.labels && params.labels.length > 0) ??
      params.temperature_livraison
    );
  }, [params.est_TAC, params.labels, params.temperature_livraison]);

  // Handle removing all filters
  const handleRemoveAllFilters = () => {
    changeMultiple({
      est_TAC: undefined,
      labels: [],
      temperature_livraison: undefined,
      page: undefined, // Reset page number
    });
  };

  // Handle individual label changes
  const handleLabelChange = (label: string, checked: boolean) => {
    const newLabels = checked
      ? [...selectedLabels, label]
      : selectedLabels.filter((l) => l !== label);
    changeMultiple({
      labels: newLabels.length > 0 ? newLabels : undefined,
      page: undefined, // Reset page number
    });
  };

  const tacCount = facets?.est_TAC?.true ?? 0;
  const positifCount = facets?.temperature_livraison?.positif;
  const negatifCount = facets?.temperature_livraison?.negatif;
  const labelEntries = facets?.labels ? Object.entries(facets.labels) : [];
  const totalItemsCount = pagination?.total_items_count ?? 0;

  return (
    <Flex
      direction='column'
      p='3'
      gap={3}
    >
      <Flex
        justify='space-between'
        flexWrap='wrap'
        gap={3}
      >
        <HStack
          divider={
            <Box
              borderLeft='1px'
              borderColor='gray.200'
              height='20px'
              mx={2}
            />
          }
          spacing={[1, 2]}
          flexWrap='wrap'
          mb={{base: 2, md: 0}}
        >
          {backoffice && (
            <Box
              height='38px'
              p='7px 11px 7px 11px'
              borderRadius='8px'
              fontWeight='400'
              color='blue.main'
              border='1px'
              borderColor='gray.light'
              bg='white'
            >
              <Checkbox
                borderColor='gray.light'
                disabled={!tacCount}
                size='sm'
                isChecked={filtersHook.params.est_TAC === true}
                onChange={(event) => {
                  changeMultiple({
                    est_TAC: event.target.checked ? true : undefined,
                    page: undefined, // Reset page number
                  });
                }}
                sx={{
                  '& .chakra-checkbox__control': {
                    borderColor: 'gray.light',
                    _checked: {
                      bg: 'blue.main',
                      borderColor: 'blue.main',
                    },
                  },
                }}
              >
                {/* eslint-disable-next-line i18next/no-literal-string */}
                <Text>TAC ({tacCount})</Text>
              </Checkbox>
            </Box>
          )}

          {/* <Box height="38px"
            p="7px 11px 7px 11px"
            borderRadius="8px"
            fontWeight="400"
            color="blue.main"
            border="1px"
            borderColor="gray.light"
            bg="white">
            <Checkbox
              borderColor="gray.light"
              size="sm"
              isChecked={filtersHook.params.est_MP}
              onChange={(event) => {
                filtersHook.change('est_MP', event.target.checked);
              }}
              sx={{
                '& .chakra-checkbox__control': {
                  borderColor: 'gray.light',
                  _checked: {
                    bg: 'blue.main',
                    borderColor: 'blue.main',
                  },
                },
              }}
            >
              <Text>MP</Text>
            </Checkbox>
          </Box> */}

          {/* Temperature de livraison dropdown */}
          <Popover>
            <PopoverTrigger>
              <Button
                variant='outline'
                size='sm'
                fontWeight='normal'
                rightIcon={<ChevronDownIcon />}
                borderColor='gray.200'
                bg='white'
                position='relative'
                whiteSpace='nowrap'
              >
                {t('catalogue.filtre.non_surgele_surgele')}
                {filtersHook.params.temperature_livraison && (
                  <Circle
                    position='absolute'
                    top='1'
                    right='1'
                    size='2'
                    bg='blue.main'
                  />
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent
              marginLeft='10px'
              width='300px'
            >
              <PopoverBody
                maxH='300px'
                overflowY='auto'
                backgroundColor='white'
                p={4}
              >
                <Flex
                  direction='column'
                  gap={2}
                >
                  {!!positifCount && (
                    <Checkbox
                      size='sm'
                      isChecked={
                        filtersHook.params.temperature_livraison === 'positif'
                      }
                      onChange={(e) => {
                        changeMultiple({
                          temperature_livraison: e.target.checked
                            ? 'positif'
                            : undefined,
                          page: undefined, // Reset page number
                        });
                      }}
                    >
                      {t('temperature_livraison.non_surgeles_uniquement')} (
                      {positifCount})
                    </Checkbox>
                  )}
                  {!!negatifCount && (
                    <Checkbox
                      size='sm'
                      isChecked={
                        filtersHook.params.temperature_livraison === 'negatif'
                      }
                      onChange={(e) => {
                        changeMultiple({
                          temperature_livraison: e.target.checked
                            ? 'negatif'
                            : undefined,
                          page: undefined, // Reset page number
                        });
                      }}
                    >
                      {t('temperature_livraison.surgeles_uniquement')} (
                      {negatifCount})
                    </Checkbox>
                  )}
                </Flex>
              </PopoverBody>
            </PopoverContent>
          </Popover>

          {/* Labels dropdown */}
          <Popover>
            <PopoverTrigger>
              <Button
                variant='outline'
                size='sm'
                fontWeight='normal'
                rightIcon={<ChevronDownIcon />}
                borderColor='gray.200'
                bg='white'
                m='0'
                position='relative'
                whiteSpace='nowrap'
              >
                {t('catalogue.filtre.labels')}
                {filtersHook.params.labels &&
                  filtersHook.params.labels.length > 0 && (
                    <Circle
                      position='absolute'
                      top='1'
                      right='1'
                      size='2'
                      bg='blue.500'
                    />
                  )}
              </Button>
            </PopoverTrigger>
            <PopoverContent
              marginLeft='10px'
              width='350px'
            >
              <PopoverBody
                maxH='300px'
                overflowY='auto'
                backgroundColor='white'
                p={4}
              >
                <Flex
                  direction='column'
                  gap={2}
                >
                  {labelEntries.map(([label, count]) => (
                    <Checkbox
                      key={label}
                      size='sm'
                      isChecked={selectedLabels.includes(label)}
                      onChange={(e) => {
                        handleLabelChange(label, e.target.checked);
                      }}
                    >
                      {label} ({count})
                    </Checkbox>
                  ))}
                  {labelEntries.length === 0 && (
                    <Text
                      fontSize='sm'
                      color='gray.500'
                    >
                      {t('catalogue.filtre.no_labels')}
                    </Text>
                  )}
                </Flex>
              </PopoverBody>
            </PopoverContent>
          </Popover>
        </HStack>

        {/* Section résultats et bouton Réinitialiser */}
        <Flex
          align='center'
          gap={3}
          flexWrap='wrap'
        >
          <Box
            borderRadius='8px'
            border='1px'
            borderColor='gray.light'
            p='7px 11px 7px 11px'
            fontSize='sm'
            color='gray.500'
          >
            {}
            <Text>
              {t('grand_compte.resultats')} {totalItemsCount}
            </Text>
          </Box>
        </Flex>
      </Flex>

      {/* Affichage des tags pour les filtres actifs */}
      {hasActiveFilters && (
        <Wrap
          spacing={2}
          mt={2}
        >
          <HStack
            spacing={2}
            alignItems='center'
          >
            <Wrap spacing={2}>
              {/* Tag for TAC */}
              {params.est_TAC && (
                <WrapItem>
                  <Tag
                    size='md'
                    bg='gray.100'
                    borderRadius='8px'
                  >
                    <TagLabel>TAC</TagLabel>
                    <TagCloseButton
                      onClick={() => {
                        changeMultiple({est_TAC: undefined, page: undefined});
                      }}
                    />
                  </Tag>
                </WrapItem>
              )}

              {/* Tags for Labels */}
              {selectedLabels.map((label) => (
                <WrapItem key={label}>
                  <Tag
                    size='md'
                    bg='gray.100'
                    borderRadius='8px'
                  >
                    <TagLabel>{label}</TagLabel>
                    <TagCloseButton
                      onClick={() => {
                        handleLabelChange(label, false);
                      }}
                    />
                  </Tag>
                </WrapItem>
              ))}

              {/* Tag for Temperature */}
              {params.temperature_livraison && (
                <WrapItem>
                  <Tag
                    size='md'
                    bg='gray.100'
                    borderRadius='8px'
                  >
                    <TagLabel>
                      {params.temperature_livraison === 'positif'
                        ? t('temperature_livraison.non_surgeles_uniquement')
                        : t('temperature_livraison.surgeles_uniquement')}
                    </TagLabel>
                    <TagCloseButton
                      onClick={() => {
                        changeMultiple({
                          temperature_livraison: undefined,
                          page: undefined,
                        });
                      }}
                    />
                  </Tag>
                </WrapItem>
              )}
            </Wrap>

            {/* Clear all filters button */}
            <Box>
              <Tooltip
                label={t('catalogue.filtre.effacer')}
                hasArrow
              >
                <Button
                  variant='unstyled'
                  px={2}
                  bg='transparent'
                  onClick={handleRemoveAllFilters}
                  display='flex'
                  alignItems='center'
                >
                  <Text
                    fontSize='small'
                    color='gray.500'
                    textDecoration='underline'
                    _hover={{textDecoration: 'none'}}
                    transition='all 0.2s'
                  >
                    {t('catalogue.filtre.tout_effacer')}
                  </Text>
                </Button>
              </Tooltip>
            </Box>
          </HStack>
        </Wrap>
      )}
    </Flex>
  );
};
